import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import EmptyState from '../components/EmptyState';
import { cmsService, BlogPost } from '../services/cms';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isTr = language === 'tr';
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    try {
      const data = await cmsService.getAllBlogPosts(false);
      setBlogPosts(data);
    } catch (error) {
      console.error('Failed to load blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isTr ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).toUpperCase();
  };

  const getTags = (tagsString?: string) => {
    if (!tagsString) return [];
    return tagsString.split(',').map(tag => tag.trim()).filter(Boolean);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · BLOG</span>
          <span className="eyebrow-mute hidden md:inline">{isTr ? 'YAZILAR' : 'WRITINGS'}</span>
        </div>

        <div className="max-w-3xl mb-20">
          <h1 className="display-h display-lg mb-6">
            {isTr ? <>Yazılar, <em>notlar.</em></> : <>Notes, <em>writings.</em></>}
          </h1>
          <p className="font-prose text-[17px] leading-relaxed text-ink-dim max-w-2xl">
            {isTr
              ? 'Senaryo, yapay zekâ ve hikâye anlatımı üzerine düşündüklerimiz. Hızlı not, derin makale, ara sıra şüphe.'
              : 'Our thoughts on screenwriting, AI and storytelling. Quick notes, deep essays, occasional doubt.'}
          </p>
        </div>

        {loading ? (
          <div className="py-16 border-t border-hairline">
            <span className="eyebrow-mute">{isTr ? 'YÜKLENİYOR…' : 'LOADING…'}</span>
          </div>
        ) : blogPosts.length === 0 ? (
          <EmptyState
            title={isTr ? 'Yakında içerik gelecek' : 'Content coming soon'}
            description={isTr
              ? 'Senaryo yazımı, yapay zekâ ve daha fazlası hakkında güncel yazılar burada yayınlanacak.'
              : 'Fresh content on screenwriting, AI and more will be published here soon.'}
          />
        ) : (
          <>
            {/* Editorial list — magazine style */}
            <div className="border-t border-hairline">
              {blogPosts.map((post, i) => {
                const tags = getTags(post.tags);
                const title = isTr ? post.title_tr : post.title_en;
                const excerpt = isTr ? (post.excerpt_tr || '') : (post.excerpt_en || '');

                return (
                  <article
                    key={post.id}
                    onClick={() => alert(isTr
                      ? `${title} blog yazısı detayı yakında eklenecek.`
                      : `Blog post details for ${title} will be added soon.`)}
                    className="grid grid-cols-12 gap-4 md:gap-8 py-10 border-b border-hairline cursor-pointer group hover:bg-accent-soft transition-colors px-2"
                  >
                    {/* Index + meta */}
                    <div className="col-span-12 md:col-span-2">
                      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink-mute mb-2">
                        /{String(i + 1).padStart(2, '0')}
                      </div>
                      <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-accent">
                        {formatDate(post.published_at || post.created_at)}
                      </div>
                    </div>

                    {/* Title + excerpt */}
                    <div className="col-span-12 md:col-span-7">
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-3 mb-3">
                          {tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-mute"
                            >
                              · {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <h2 className="font-serif text-3xl md:text-4xl text-ink leading-[1.05] tracking-[-0.02em] mb-4 group-hover:italic group-hover:text-accent transition-all">
                        {title}
                      </h2>
                      {excerpt && (
                        <p className="font-prose text-[15px] leading-relaxed text-ink-dim line-clamp-3 max-w-2xl">
                          {excerpt}
                        </p>
                      )}
                    </div>

                    {/* Author + read more */}
                    <div className="col-span-12 md:col-span-3 md:text-right">
                      <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-mute mb-2">
                        {isTr ? 'YAZAR' : 'BY'}
                      </div>
                      <div className="font-prose text-[14px] text-ink mb-6">{post.author}</div>
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">
                        {isTr ? 'OKU' : 'READ'} <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* CTA */}
            <div className="text-center mt-24 pt-16 max-w-2xl mx-auto">
              <span className="eyebrow mb-4 block">{isTr ? 'BAŞLA' : 'BEGIN'}</span>
              <h2 className="display-h display-md mb-6">
                {isTr ? <>Senaryona <em>aynayı</em> tut.</> : <>Hold the <em>mirror</em> to your script.</>}
              </h2>
              <button onClick={() => navigate('/register')} className="btn-accent">
                {isTr ? 'ÜCRETSIZ BAŞLA' : 'START FREE'}
                <span aria-hidden>→</span>
              </button>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
